import AccountMenu from '@/components/nav/account-menu';
import NavBrand from '@/components/nav/nav-brand';
import NavBreadcrumb, { type Crumb } from '@/components/nav/nav-breadcrumb';
import QuickSettingsPopover from '@/components/nav/quick-settings-popover';
import { useCollectionTitle } from '@/components/nav/use-collection-title';

interface ExperienceNavbarProps {
  /** Unused for the trail now — the title bar below shows it — but kept so the bar can fall back
   *  to it when the experience has no collection to point at. */
  title: string;
  /** Every destination here, guarded: leaving with an unsaved change raises the modal. */
  onNavigate: (to: string) => void;
  /** Same guard, for exits that are not plain URLs — logout clears the token on its way. */
  onGuard?: (run: () => void) => void;
  collectionId?: string | null;
}

/**
 * The experience board's top bar: the logo, and the one level above this page.
 *
 * It had the same duplication the task bar did, and worse — the logo, the back chevron AND the
 * Home crumb were three controls pointing at `/app`. Removed:
 *
 * - **The back chevron** — it went to `/app`, which is where the logo goes. Its unsaved-changes
 *   guard was the one thing it owned; that now sits on `onNavigate`, which every destination
 *   here (the logo included) routes through.
 * - **The Home crumb** — same destination as the logo, side by side with it.
 * - **The experience's own title** — `ExperienceTitleBar` renders it directly below, editable.
 *
 * What is left is the rule the three bars share: show the logo and the page's PARENT, never the
 * page itself and never Home. For a collection-less experience the parent IS home, so the trail
 * is empty and the logo alone carries it.
 */
export default function ExperienceNavbar({ title: _title, onNavigate, onGuard, collectionId }: ExperienceNavbarProps) {
  const collectionTitle = useCollectionTitle(collectionId);

  const crumbs: Crumb[] = collectionId
    ? [{ label: collectionTitle ?? '…', to: `/app/collection/${collectionId}` }]
    : [];

  return (
    <header className='flex h-14 shrink-0 items-center gap-1 border-b border-ws-border bg-ws-surface px-2 sm:px-3'>
      <NavBrand className='h-9' onNavigate={onNavigate} />

      <div className='flex min-w-0 flex-1 items-center'>
        {crumbs.length > 0 && <NavBreadcrumb items={crumbs} onNavigate={onNavigate} />}
      </div>

      <div className='flex shrink-0 items-center'>
        <QuickSettingsPopover compact />
        <AccountMenu compact onGuard={onGuard} />
      </div>
    </header>
  );
}
